/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import cx from 'classnames';

import s from './styles.module.scss';

interface TextAreaField {
  onChange: (...args: unknown[]) => void;
  value?: string;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
}

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  field: TextAreaField;
  innerRef?: ((textarea: HTMLTextAreaElement | null) => void) | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: any;
  autoresize?: boolean;
  showCounter?: boolean;
  wrapperClassName?: string;
  error?: string;
  errorColor?: string;
}

class TextArea extends React.Component<TextAreaProps> {
  static defaultProps = {
    innerRef: null,
    autoresize: false,
    showCounter: false,
  };

  private textarea!: HTMLTextAreaElement;

  handleResize = (...args: unknown[]) => {
    this.textarea.style.height = 'inherit';

    const computed = window.getComputedStyle(this.textarea);
    const height =
      this.textarea.scrollHeight -
      parseInt(computed.getPropertyValue('padding-top'), 10) -
      parseInt(computed.getPropertyValue('padding-bottom'), 10);

    this.textarea.style.height = `${height}px`;
    this.props.field.onChange(...args);
  };

  bindRef = (textarea: HTMLTextAreaElement | null) => {
    if (this.props.innerRef) this.props.innerRef(textarea);
    if (textarea) this.textarea = textarea;
  };

  render() {
    const {
      field: { onChange, ...fieldProps },
      innerRef,
      form,
      autoresize,
      showCounter,
      ...props
    } = this.props;

    return (
      <div className={cx(s.wrapper, props.wrapperClassName)}>
        <textarea
          ref={this.bindRef}
          onChange={this.props.autoresize ? this.handleResize : onChange}
          {...fieldProps}
          {...props}
          className={cx(s.textArea, props.className, props.error && s.textAreaError)}
        />
        <div className={s.footer}>
          <span className={cx(s.footerError, props.errorColor)}>{props.error}</span>
          {showCounter && (
            <span className={s.footerCounter}>{`${fieldProps.value?.length || 0} / ${props.maxLength}`}</span>
          )}
        </div>
      </div>
    );
  }
}

export default TextArea;
